/**
 * The API examples published on the homepage and in the Submit tool.
 *
 * Data lives here rather than in the component so a test can import it without pulling React and the
 * router into the test process, following the same split as `host-shaped-query` and `scan-link`.
 *
 * EVERY COMMAND HERE MUST WORK WITH NO CREDENTIALS. Each was executed against production before being
 * published (submit 202, hash lookup 200, URL scan 202, result 200 on 2026-08-19), and
 * `api-examples.test.ts` cross-checks the endpoint list against the paths the OpenAPI spec declares
 * anonymous, so an endpoint that later gains an auth requirement fails the build rather than the
 * reader's first attempt.
 */

/** A real public sample, so the lookup example returns a report instead of a 404. */
export const SAMPLE_HASH = '05fcb36d0d0f42ca168661001634bb5cf9efb8dd4a4448c2913d4c7080a42888';

export interface ApiExample {
  id: string;
  label: string;
  /** Endpoint as `METHOD /path`, pinned by the test against the anonymous set in the spec. */
  endpoint: string;
  note: string;
  command: string;
}

export const API_EXAMPLES: readonly ApiExample[] = [
  {
    id: 'submit',
    label: 'Submit a file',
    endpoint: 'POST /v1/submit',
    // The body shape is load-bearing: a multipart `-F file=@...` upload returns HTTP 500, so an
    // example written that way would fail on the reader's very first attempt.
    note: 'Returns a task id and the report URL. Base64 in JSON, not a multipart upload.',
    command: `curl -X POST https://malwareanalyzer.com/v1/submit \\
  -H 'content-type: application/json' \\
  -d "{\\"fileName\\":\\"sample.bin\\",\\"fileBase64\\":\\"$(base64 < sample.bin | tr -d '\\n')\\"}"`,
  },
  {
    id: 'hash',
    label: 'Check a hash',
    endpoint: 'GET /v1/files/{sha256}',
    note: 'Returns the full report if we have seen the file, or 404 if we have not.',
    command: `curl https://malwareanalyzer.com/v1/files/${SAMPLE_HASH}`,
  },
  {
    id: 'url',
    label: 'Scan a URL',
    endpoint: 'POST /v1/scan',
    note: 'Returns a scan id immediately; the scan continues in the background.',
    command: `curl -X POST https://malwareanalyzer.com/v1/scan \\
  -H 'content-type: application/json' \\
  -d '{"url":"http://example.com"}'`,
  },
  {
    id: 'result',
    label: 'Get the result',
    endpoint: 'GET /v1/result/{uuid}',
    note: 'Poll the scan id from the previous call. This read is not metered.',
    command: 'curl https://malwareanalyzer.com/v1/result/scan_your_id_here',
  },
];

/** Stated on the card. Submissions are unlimited; anonymous lookups are capped per IP per day. */
export const ANON_LOOKUP_QUOTA_PER_DAY = 1000;
